import React from 'react'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import { fetchStandardReuseImpact } from '../../services/complianceReuseApi'
import { getStandardDisplayName } from '../../utils/complianceStandard'

type Props = {
  informationSystemId: string
  standardCodes: string[]
}

export default function InformationSystemCrossStandardReusePanel({ standardCodes }: Props) {
  const { t } = useI18n()
  const [impacts, setImpacts] = React.useState<
    Array<{ standardCode: string; suggestedReuseCount: number; partnerStandards: string[] }>
  >([])

  React.useEffect(() => {
    if (!featureFlags.COMPLIANCE_REUSE_V1 || !standardCodes.length) return
    void Promise.all(
      standardCodes.slice(0, 5).map(async (code) => {
        try {
          const data = (await fetchStandardReuseImpact(code)) as {
            standardCode: string
            suggestedReuseCount?: number
            partnerStandards?: string[]
          }
          return {
            standardCode: data.standardCode || code,
            suggestedReuseCount: data.suggestedReuseCount || 0,
            partnerStandards: data.partnerStandards || [],
          }
        } catch {
          return { standardCode: code, suggestedReuseCount: 0, partnerStandards: [] }
        }
      }),
    ).then(setImpacts)
  }, [standardCodes.join(',')])

  if (!featureFlags.COMPLIANCE_REUSE_V1) return null

  const totalSuggested = impacts.reduce((n, i) => n + i.suggestedReuseCount, 0)

  return (
    <GovernanceListingSection
      title={t('complianceReuse.crossStandardImpact', 'Cross-standard impact')}
      description={t(
        'complianceReuse.isPanelHint',
        'Standards impacted by shared controls and reuse opportunities for this system scope.',
      )}
    >
      {totalSuggested > 0 ? (
        <p className="text-xs text-slate-700 m-0">
          {t('complianceReuse.reuseOpportunities', 'Reuse opportunities')}: {totalSuggested}
        </p>
      ) : (
        <p className="text-xs text-slate-500 m-0">{t('complianceReuse.noReuseForSystem', 'No reuse suggestions in current scope.')}</p>
      )}
      <ul className="mt-2 space-y-1 m-0 p-0 list-none">
        {impacts
          .filter((i) => i.partnerStandards.length > 0 || i.suggestedReuseCount > 0)
          .map((i) => (
            <li key={i.standardCode} className="text-xs text-slate-600">
              {getStandardDisplayName(t, i.standardCode)} — {i.partnerStandards.join(', ') || '—'}
            </li>
          ))}
      </ul>
    </GovernanceListingSection>
  )
}
